import type { ApolloQueryResult } from '@apollo/client';
import { cloneDeep } from 'lodash';
import React, {
    Dispatch,
    forwardRef,
    SetStateAction,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useState
} from 'react';
import { ActivityInput } from 'sr-types/lib/production/v1/graphql';
import { UserContext } from '../../common/auth/UserContext';
import { FormProvider } from '../../common/form/FormContext';
import { useBrowserOrAccountLocale } from '../../common/hooks/useBrowserOrAccountLocale';
import { searchClient } from '../../common/list/slimQuery';
import { AppDefinition } from '../../common/nav/apps';
import { useConfigProductionScheduleRefetchNeeded } from '../../common/utils/atoms/useConfigProductionScheduleRefetchNeeded';
import { areAllFiltersInList, failedToastMessage } from '../../common/utils/commonUtils';
import { updateSchedulerProductionData } from '../../scheduler/scheduler';
import { toSchedulerActivityItemsFromProduction } from '../../scheduler/schedulerUtil';
import {
    Activity_Field_Names,
    ActivityFormType,
    EMPTY_ACTIVITY,
    EMPTY_DEPENDENCIES,
    getActivityListeners,
    getActivityValidators,
    getDependencyArrayFromState,
    handleActivitySaveInput,
    reverseDependencyArrayToObject
} from '../helpers/activityUtils';
import { client, detailsQuery } from '../helpers/production';
import {
    useActivityData,
    useActivitySave,
    useLinkResourceToActivity,
    useResourcesData,
    useUnlinkResourceFromActivity
} from '../helpers/productionApi';
import ActivityEditor from './ActivityEditor';
import { OPTIMISTIC_ID_PREFIX } from './activityConstants';

interface ActivityFormProps {
    open?: boolean;
    setIsOpen?: Dispatch<SetStateAction<boolean>>;
    onClose?: () => void;
    planId: string;
    productionId: string;
    isCreate?: boolean;
    activityId?: string;
    setActivityId?: (id: string) => void;
    prepopulatedActivity?: ActivityFormType;
    setPrepopulatedActivity?: (activity: ActivityFormType) => void;
    showBasic?: boolean;
    isInModal?: boolean;
    setIsSaveDisabled?: Dispatch<SetStateAction<boolean>>;
    setIsRefreshing?: Dispatch<SetStateAction<boolean>>;
    isResourceForm?: boolean;
    onDataChange?: (updatedItem) => void;
    shouldRefetch?: boolean;
    //trigger a refetch of production activities after successful save
    refetchActivityData?: () => Promise<ApolloQueryResult<any>>;
}

export type SetIsSchedulerProductionRefetchNeeded = ReturnType<typeof useConfigProductionScheduleRefetchNeeded>[1];

export const refetchActivities = (
    activityIds: string[] = [],
    isInModal: boolean,
    isResourceForm: boolean,
    activeOrganizationAccount: string,
    application: AppDefinition,
    setIsSchedulerProductionRefetchNeeded: SetIsSchedulerProductionRefetchNeeded
) => {
    if (!activityIds.length) return;

    setTimeout(() => {
        searchClient
            .refetchQueries({
                include: ['slimResultsQuery'],
                onQueryUpdated(observableQuery) {
                    return areAllFiltersInList(observableQuery.options.variables.filters, [
                        { identifier: 'entity', value: 'Activity' }
                    ]);
                }
            })
            .then((response) => {
                const items = response?.[0]?.data?.results?.hits?.items;
                if (!(isInModal || isResourceForm) || !items?.length) return;

                const matched = items.filter((it) => activityIds.includes(it.id));
                if (!matched.length) return;

                updateSchedulerProductionData(
                    matched.map(toSchedulerActivityItemsFromProduction),
                    activeOrganizationAccount,
                    application,
                    setIsSchedulerProductionRefetchNeeded
                );
            });
    }, 1000);
};

const ActivityForm = forwardRef((props: ActivityFormProps, ref) => {
    const {
        open = false,
        setIsOpen = undefined,
        onClose,
        planId,
        productionId,
        isCreate = false,
        activityId = undefined,
        setActivityId,
        prepopulatedActivity = undefined,
        showBasic = false,
        isInModal = true,
        setIsSaveDisabled = undefined,
        setIsRefreshing = undefined,
        isResourceForm = false,
        onDataChange = undefined,
        shouldRefetch = false,
        refetchActivityData
    } = props;
    const { activeOrganizationAccount, application, userProfile } = useContext(UserContext);
    const { activitySave, isActivitySaving } = useActivitySave();
    const { activityData } = useActivityData(activityId, !activityId || isCreate);
    const { linkResourceToActivity } = useLinkResourceToActivity();
    const { unlinkResourceFromActivity } = useUnlinkResourceFromActivity();
    const { resourcesData } = useResourcesData(productionId);
    const [_, setIsSchedulerProductionRefetchNeeded] = useConfigProductionScheduleRefetchNeeded();

    const [state, setState] = useState<ActivityFormType>({
        ...cloneDeep(EMPTY_ACTIVITY),
        activityOwner: {
            ...userProfile.personReference,
            label: userProfile.name.firstName + ' ' + userProfile.name.lastName
        }
    });
    const locale = useBrowserOrAccountLocale();

    const keysToHide = ['productionReference', 'productReferences', 'organizationReference'];

    const hiddenFields = keysToHide.reduce(
        (acc, key) => {
            if (Activity_Field_Names[key]) {
                acc[Activity_Field_Names[key]] = true;
            }
            return acc;
        },
        {} as Record<string, boolean>
    );

    const resourceOptions = useMemo(() => {
        if (resourcesData?.results?.hits?.items) {
            const items = resourcesData.results.hits.items;
            return items.map((item) => {
                return { id: item.id, label: item.name };
            });
        }
        return [];
    }, [resourcesData]);

    const saveActivity = async (onSuccess = undefined) => {
        const { associateWithResource, dependencies } = state;
        const activityInput: ActivityInput = handleActivitySaveInput(state, activityId);

        const tempId =
            activityId ||
            OPTIMISTIC_ID_PREFIX +
                (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                    ? crypto.randomUUID()
                    : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
        const optimisticData = { ...activityInput, identity: { id: tempId } };

        if (onDataChange) {
            onDataChange(optimisticData);
        }

        setIsOpen?.(false);

        try {
            const response: { data?: { saveActivity?: { id?: string } } } = await activitySave(
                productionId,
                planId,
                activityInput,
                !!activityId
            );

            if (!response?.data?.saveActivity?.id) {
                throw new Error('Failed to save activity');
            }

            if (setActivityId) {
                setActivityId('');
            }

            const actId = response.data.saveActivity.id;

            const { linkedResources } = activityData?.activity || {};
            const alreadyLinkedIds =
                Array.isArray(linkedResources) && linkedResources.length
                    ? linkedResources.map((item) => item.identity.id)
                    : [];
            const associateWithResourceId =
                Array.isArray(associateWithResource) && associateWithResource.length
                    ? associateWithResource.map((item) => item.id)
                    : [];

            const toUnlink = alreadyLinkedIds.filter((id) => !associateWithResourceId.includes(id));
            const toLink = associateWithResourceId.filter((id) => !alreadyLinkedIds.includes(id));

            if (toLink.length) {
                await Promise.all(toLink.map((id) => linkResourceToActivity(actId, id)));
            }

            if (toUnlink.length) {
                await Promise.all(toUnlink.map((id) => unlinkResourceFromActivity(actId, id)));
            }

            if (!activityId) {
                activityInput.activityDependencies = getDependencyArrayFromState(dependencies, actId);
                const res: { data?: { saveActivity?: { id?: string } } } = await activitySave(
                    productionId,
                    planId,
                    { ...activityInput, identity: { id: actId } },
                    true
                );

                if (!res?.data?.saveActivity?.id) {
                    throw new Error('Failed to save activity dependencies');
                }
            }

            const finalData = { ...activityInput, identity: { id: actId }, shouldRemovePendingStyle: true };

            if (shouldRefetch || activityId === undefined) {
                refetchActivities(
                    [actId],
                    isInModal,
                    isResourceForm,
                    activeOrganizationAccount,
                    application,
                    setIsSchedulerProductionRefetchNeeded
                );
            } else if (onDataChange) {
                onDataChange(finalData);
            }
            onSuccess?.(finalData);

            return actId;
        } catch (error) {
            // Rollback optimistic update on failure
            if (onDataChange && activityId) {
                // For updates: revert to original data
                if (activityData?.activity) {
                    const originalActivity = activityData.activity as any;
                    const rollbackItem = {
                        ...originalActivity,
                        identity: {
                            ...(originalActivity.identity ?? {}),
                            id: originalActivity.identity?.id ?? originalActivity.id
                        }
                    };
                    onDataChange(rollbackItem);
                }
            }

            failedToastMessage('production.activitySave.error');
        } finally {
            setIsRefreshing?.(true);

            try {
                await client.refetchQueries({
                    include: [detailsQuery]
                });
                await refetchActivityData?.();
            } catch (error) {
            } finally {
                setIsRefreshing?.(false);
            }
        }
    };

    useImperativeHandle(ref, () => ({
        handleSave: saveActivity
    }));

    useEffect(() => {
        if (prepopulatedActivity) {
            setState((prevVal) => {
                return {
                    ...prevVal,
                    ...prepopulatedActivity
                };
            });
        }
    }, [prepopulatedActivity]);

    useEffect(() => {
        if (activityData?.activity) {
            const {
                name,
                activityType,
                category,
                dateRange,
                durationSpecified,
                identity,
                locations,
                linkedResources,
                dependencies,
                activityOwner,
                activityStatus,
                tags,
                summary
            } = activityData.activity;

            const owner = activityOwner || {
                ...userProfile.personReference,
                label: userProfile.name.firstName + ' ' + userProfile.name.lastName
            };
            const associateWithResource =
                Array.isArray(linkedResources) && linkedResources.length
                    ? linkedResources.map((item) => {
                          return {
                              id: item.identity.id,
                              label: item.name
                          };
                      })
                    : [];

            setState((prevVal) => {
                return {
                    ...prevVal,
                    name,
                    activityType,
                    category,
                    dateRange,
                    durationSpecified,
                    identity,
                    locations,
                    associateWithResource,
                    activityOwner: owner,
                    activityStatus,
                    tags: tags || [],
                    summary,
                    dependencies: {
                        ...reverseDependencyArrayToObject(dependencies, activityId),
                        ...cloneDeep(EMPTY_DEPENDENCIES)
                    }
                };
            });
        }
    }, [activityData, activityId, userProfile]);

    return (
        <FormProvider
            state={state}
            setState={setState}
            validationRules={{ ...getActivityValidators() }}
            listeners={[...getActivityListeners(locale)]}
        >
            <ActivityEditor
                isInModal={isInModal}
                showBasic={showBasic}
                isOpen={open}
                setIsOpen={setIsOpen}
                onClose={onClose}
                handleSave={saveActivity}
                isLoading={activityId && !activityData?.activity}
                resourceOptions={resourceOptions}
                productionId={productionId}
                activityId={activityId}
                isSaving={isActivitySaving}
                setIsSaveDisabled={setIsSaveDisabled}
                isResourceForm={isResourceForm}
                hiddenFields={hiddenFields}
            />
        </FormProvider>
    );
});

export default ActivityForm;
